import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

export interface ChoiceCell {
  value: string;
  title: string;
  css?: string;
}

export interface CompositeCell {
  primary?: string;
  secondary?: string;
}

export interface StackedCell {
  top?: string;
  bottom?: string;
}

/** Attempt record stored in `responses` (shape depends on the processor). */
export type MessageAttempt = Record<string, unknown>;

/** MessageQueue detail payload (secrets/OTPs are redacted server-side). */
export interface MessageQueueItem {
  id: string;
  channel: ChoiceCell;
  application: ChoiceCell;
  status: ChoiceCell;
  delivery_status: ChoiceCell;
  subject: string;
  message: string;
  recipient: string;
  recipient_masked: string;
  language: string;
  processor: string;
  processor_provider: CompositeCell | null;
  country: CompositeCell | null;
  merchant_application: CompositeCell | null;
  campaign: CompositeCell | null;
  attempt_count: number;
  max_retries: number;
  last_error: string;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
  request_payload: Record<string, unknown>;
  responses: MessageAttempt[];
  delivery_confirmation: Record<string, unknown>;
}

/** Query of `GET auth/message-queues/analytics/`. */
export interface MessageQueueAnalyticsFilters {
  period?: string;
  start_date?: string;
  end_date?: string;
  channel?: string;
  status?: string;
  processor_provider?: string;
  country?: string;
  merchant?: string;
  application?: string;
  compare?: boolean;
}

export interface MqSummary {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  processing: number;
  retrying: number;
  success_rate: number | null;
  avg_attempts: number;
  avg_latency_seconds: number | null;
  p50_latency_seconds: number | null;
  p95_latency_seconds: number | null;
}

export interface MqBreakdownRow {
  id: string | null;
  key: string;
  name: string;
  total: number;
  sent: number;
  failed: number;
  in_progress: number;
  success_rate: number | null;
}

export type MqBreakdownDimension = 'channel' | 'provider' | 'country' | 'merchant' | 'application';

export interface MessageQueueAnalytics {
  period: { start: string | null; end: string };
  summary: MqSummary;
  timeseries: {
    granularity: 'hour' | 'day';
    points: Array<{
      bucket: string;
      total: number;
      sent: number;
      failed: number;
      pending: number;
      processing: number;
      retrying: number;
    }>;
  };
  breakdowns: Record<MqBreakdownDimension, MqBreakdownRow[]>;
  errors: Array<{ error: string; count: number }>;
  retries: Array<{ attempts: number; count: number }>;
  previous: {
    period: { start: string; end: string };
    summary: MqSummary;
    deltas: Record<string, number | null>;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class MessageQueueService {
  private readonly api = inject(ApiService);
  private readonly path = 'auth/message-queues';

  /** Dashboard aggregates; empty filter values are not sent. */
  getAnalytics(filters: MessageQueueAnalyticsFilters): Observable<MessageQueueAnalytics> {
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        params[key] = String(value);
      }
    }
    return this.api.get<MessageQueueAnalytics>(`${this.path}/analytics/`, params);
  }

  getMessage(id: string): Observable<MessageQueueItem> {
    return this.api.get<MessageQueueItem>(`${this.path}/${id}/`);
  }

  /** Resend a FAILED or SENT message. */
  resend(id: string): Observable<MessageQueueItem> {
    return this.api.post<MessageQueueItem>(`${this.path}/${id}/resend/`, {});
  }
}
